/**
 * useAccountStatus — the account-lifecycle gate, resolved once for the app shell.
 *
 * Shaped after `useOnboardingStatus`, and for the same reason: the shell folds every gate into
 * ONE loading surface, so each gate has to be a decision the shell can read, not a component that
 * renders its own loader in its own box.
 *
 * This is a THIRD gate, deliberately separate from `useCapabilities`:
 *   · capabilities answer "what is this account allowed to do?" (role + org position)
 *   · this answers "may this account act at all right now?" (`profiles.status`)
 * A pending buyer holds every buyer capability and is still refused, so folding the two together
 * would make one of the two questions unanswerable.
 *
 * FAILS CLOSED, matching OnboardingGuard. A profile fetch that keeps erroring resolves to
 * "blocked" rather than passing the user through — an unreadable profile is exactly the state an
 * un-approved account would be in if something went wrong, and the failure mode of guessing
 * "allowed" is an unvetted account inside the product.
 *
 * `profiles` is exempt from the database's restrictive approval policy precisely so this read
 * still works for a blocked account. Without that exemption this hook could never see the status
 * it exists to read.
 */
import { useCallback, useEffect, useState } from "react";

import { isAccessBlockedStatus } from "@/lib/account-status";
import { UserCacheService } from "@/lib/user-cache";
import { useAuth } from "@/providers/AuthProvider";

export type AccountAccess = "loading" | "allowed" | "blocked";

export interface AccountStatusResult {
  access: AccountAccess;
  /** The raw `profiles.status`. Trust it only once `access !== "loading"`. */
  status: string | null;
  /** Re-read the profile, bypassing the 60s cache. The waiting page's "Check again". */
  refresh: () => Promise<void>;
}

// Mirrors OnboardingGuard: one retry, so a transient network blip does not park a
// perfectly good account on the waiting page — while a persistent failure still fails closed.
const PROFILE_FETCH_ATTEMPTS = 2;

export function useAccountStatus(): AccountStatusResult {
  const { user, loading: authLoading } = useAuth();
  const [access, setAccess] = useState<AccountAccess>("loading");
  const [status, setStatus] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const userId = user?.id ?? null;

  useEffect(() => {
    if (authLoading) return;
    // No session is not a blocked account — ShellGate sends them to login, and answering
    // "blocked" here would park a signed-out visitor on the waiting page instead.
    if (!userId) {
      setAccess("allowed");
      setStatus(null);
      return;
    }

    let cancelled = false;

    (async () => {
      for (let attempt = 0; attempt < PROFILE_FETCH_ATTEMPTS; attempt++) {
        try {
          const profile = await UserCacheService.getProfile(userId);
          if (cancelled) return;

          const nextStatus = profile?.status ?? null;
          setStatus(nextStatus);
          setAccess(isAccessBlockedStatus(nextStatus) ? "blocked" : "allowed");
          return;
        } catch {
          if (cancelled) return;
          // The final attempt MUST set a state, or the shell hangs on a loader forever.
          if (attempt === PROFILE_FETCH_ATTEMPTS - 1) {
            setStatus(null);
            setAccess("blocked");
          }
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [userId, authLoading, reloadKey]);

  const refresh = useCallback(async () => {
    if (!userId) return;
    // Bust the 60s cache first — otherwise "Check again" re-reads the same stale row and the
    // button appears to do nothing for the first minute after an admin approves.
    UserCacheService.clearCache(userId);
    setReloadKey((k) => k + 1);
  }, [userId]);

  return {
    access: authLoading ? "loading" : access,
    status,
    refresh,
  };
}
